import { useState } from "react";
import { motion } from "framer-motion";
import { Gift, Building2, Loader2, CheckCircle2, X } from "lucide-react";
import { useNavigate } from "@/lib/router-compat";
import { useLifeMart } from "@/hooks/useLifeMart";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

type Mode = null | "code" | "refer" | "success-code" | "success-refer";

const HomieLifeMartCard = () => {
  const navigate = useNavigate();
  const { activateAccess } = useLifeMart();
  const { user } = useAuth();

  const [mode, setMode] = useState<Mode>(null);
  const [code, setCode] = useState("");
  const [employer, setEmployer] = useState("");
  const [hrName, setHrName] = useState("");
  const [hrEmail, setHrEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await activateAccess(code);
    setSubmitting(false);
    if (!result.success) {
      setError(result.error || "Could not activate code.");
      return;
    }
    setMode("success-code");
    toast.success("LifeMart access activated! 🎉");
    setTimeout(() => {
      navigate(user ? "/dashboard" : "/auth?src=lifemart");
    }, 1600);
  };

  const handleRefer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employer.trim()) {
      setError("Please enter your employer name.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const { error: insertError } = await supabase.from("employer_referrals").insert({
      employer_name: employer.trim(),
      contact_name: hrName.trim() || null,
      contact_email: hrEmail.trim() || null,
      user_id: user?.id ?? null,
    });
    setSubmitting(false);
    if (insertError) {
      setError("Couldn't submit referral. Try again.");
      return;
    }
    setMode("success-refer");
    toast.success("Thanks! We'll reach out to your employer.");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5 p-4 shadow-xs max-w-[95%]"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xl">
          🏠
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-foreground text-sm leading-tight">
            Heads up — your employer might cover this!
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            Home Approach is <strong className="text-foreground">free</strong> for LifeMart members, plus you get{" "}
            <strong className="text-primary">25 bonus points</strong> just for joining.
          </p>
        </div>
      </div>

      {/* Default state — two action buttons */}
      {mode === null && (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => { setMode("code"); setError(null); }}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
          >
            <Gift className="h-3.5 w-3.5" />
            I have a code
          </button>
          <button
            onClick={() => { setMode("refer"); setError(null); }}
            className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-primary/30 bg-background px-3 py-2.5 text-xs font-semibold text-primary hover:bg-primary/5 transition-colors"
          >
            <Building2 className="h-3.5 w-3.5" />
            Refer my employer
          </button>
        </div>
      )}

      {/* Code redemption form */}
      {mode === "code" && (
        <form onSubmit={handleRedeem} className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-foreground">Enter your LifeMart code</label>
            <button type="button" onClick={() => setMode(null)} className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. LIFEMART2025"
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm uppercase placeholder:normal-case placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
            autoFocus
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
          <button
            type="submit"
            disabled={submitting || !code.trim()}
            className="w-full flex items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2.5 text-xs font-semibold text-primary-foreground disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gift className="h-3.5 w-3.5" />}
            Activate access
          </button>
        </form>
      )}

      {/* Refer employer form */}
      {mode === "refer" && (
        <form onSubmit={handleRefer} className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-foreground">Refer your employer</label>
            <button type="button" onClick={() => setMode(null)} className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <input
            value={employer}
            onChange={(e) => setEmployer(e.target.value)}
            placeholder="Employer name *"
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
            autoFocus
          />
          <input
            value={hrName}
            onChange={(e) => setHrName(e.target.value)}
            placeholder="HR contact name (optional)"
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
          />
          <input
            type="email"
            value={hrEmail}
            onChange={(e) => setHrEmail(e.target.value)}
            placeholder="HR contact email (optional)"
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2.5 text-xs font-semibold text-primary-foreground disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Building2 className="h-3.5 w-3.5" />}
            Submit referral (+15 pts)
          </button>
        </form>
      )}

      {/* Success states */}
      {mode === "success-code" && (
        <div className="flex items-center gap-2 rounded-xl bg-primary/10 px-3 py-2.5">
          <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
          <p className="text-xs font-semibold text-foreground">Activated! Redirecting you to sign up…</p>
        </div>
      )}
      {mode === "success-refer" && (
        <div className="flex items-center gap-2 rounded-xl bg-primary/10 px-3 py-2.5">
          <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
          <p className="text-xs font-semibold text-foreground">Got it — we'll be in touch with {employer} soon.</p>
        </div>
      )}
    </motion.div>
  );
};

export default HomieLifeMartCard;
